import React, { useState } from 'react';
import './Reports.css';

interface ActivityLog {
  id: string;
  timestamp: string;
  user: string;
  userType: 'admin' | 'manager' | 'affiliate' | 'advertiser' | 'system';
  action: string;
  actionType: 'create' | 'update' | 'delete' | 'login' | 'logout' | 'view' | 'approve' | 'reject' | 'block' | 'activate' | 'system';
  entity: string;
  entityType: 'offer' | 'affiliate' | 'advertiser' | 'payout' | 'conversion' | 'user' | 'system' | 'report';
  entityId?: number;
  details: string;
  ipAddress: string;
  userAgent: string;
  location?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  status: 'success' | 'failed' | 'warning';
}

const ActivityLog: React.FC = () => {
  const [dateRange, setDateRange] = useState({
    startDate: '2023-08-28',
    endDate: '2023-08-30'
  });
  const [userFilter, setUserFilter] = useState<string>('all');
  const [actionFilter, setActionFilter] = useState<string>('all');
  const [entityFilter, setEntityFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const [activities] = useState<ActivityLog[]>([
    {
      id: 'log_001',
      timestamp: '2023-08-30 14:32:15',
      user: 'admin@adbizz.com',
      userType: 'admin',
      action: 'Approved conversion',
      actionType: 'approve',
      entity: 'Conversion conv_001',
      entityType: 'conversion',
      entityId: 1,
      details: 'Approved conversion for TikTok App Install - iOS (Affiliate: Marvel Ads, Amount: $2.50)',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
      location: 'New York, US',
      severity: 'medium',
      status: 'success'
    },
    {
      id: 'log_002',
      timestamp: '2023-08-30 13:45:22',
      user: 'manager1@adbizz.com',
      userType: 'manager',
      action: 'Created custom payout',
      actionType: 'create',
      entity: 'Custom Payout #4',
      entityType: 'payout',
      entityId: 4,
      details: 'Created custom payout for Marvel Ads - Subscription Service Sale: $18.50 (was $15.00)',
      ipAddress: '192.168.1.101',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
      location: 'London, UK',
      severity: 'low',
      status: 'success'
    },
    {
      id: 'log_003',
      timestamp: '2023-08-30 12:18:44',
      user: 'marvel@marvelads.com',
      userType: 'affiliate',
      action: 'Login attempt',
      actionType: 'login',
      entity: 'User Session',
      entityType: 'user',
      details: 'Successful login from affiliate dashboard',
      ipAddress: '203.0.113.42',
      userAgent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 16_0 like Mac OS X) AppleWebKit/605.1.15',
      location: 'Los Angeles, US',
      severity: 'low',
      status: 'success'
    },
    {
      id: 'log_004',
      timestamp: '2023-08-30 11:55:33',
      user: 'system',
      userType: 'system',
      action: 'Fraud detection alert',
      actionType: 'system',
      entity: 'Conversion conv_006',
      entityType: 'conversion',
      entityId: 6,
      details: 'Suspicious activity detected: Multiple conversions from same IP within 5 minutes. Conversion blocked automatically.',
      ipAddress: '192.168.1.105',
      userAgent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 15_0 like Mac OS X) AppleWebKit/605.1.15',
      location: 'Paris, FR',
      severity: 'high',
      status: 'warning'
    },
    {
      id: 'log_005',
      timestamp: '2023-08-30 10:22:11',
      user: 'admin@adbizz.com',
      userType: 'admin',
      action: 'Updated offer status',
      actionType: 'update',
      entity: 'Education App - Premium',
      entityType: 'offer',
      entityId: 6,
      details: 'Changed offer status from Active to Paused due to advertiser budget limits',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
      location: 'New York, US',
      severity: 'medium',
      status: 'success'
    },
    {
      id: 'log_006',
      timestamp: '2023-08-29 16:45:22',
      user: 'tiktok@tiktok.com',
      userType: 'advertiser',
      action: 'Viewed conversion report',
      actionType: 'view',
      entity: 'Conversion Report',
      entityType: 'report',
      details: 'Accessed conversion report for TikTok App Install - iOS (Date range: 2023-08-01 to 2023-08-29)',
      ipAddress: '198.51.100.15',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
      location: 'Singapore, SG',
      severity: 'low',
      status: 'success'
    },
    {
      id: 'log_007',
      timestamp: '2023-08-29 15:33:18',
      user: 'admin@adbizz.com',
      userType: 'admin',
      action: 'Rejected conversion',
      actionType: 'reject',
      entity: 'Conversion conv_004',
      entityType: 'conversion',
      entityId: 4,
      details: 'Rejected conversion for Finance App Test Campaign - Invalid attribution window (Affiliate: CrowMobi Premium)',
      ipAddress: '192.168.1.100',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
      location: 'New York, US',
      severity: 'medium',
      status: 'success'
    },
    {
      id: 'log_008',
      timestamp: '2023-08-29 14:12:44',
      user: 'crowmobi@crowmobi.com',
      userType: 'affiliate',
      action: 'Failed login attempt',
      actionType: 'login',
      entity: 'User Session',
      entityType: 'user',
      details: 'Failed login attempt - Invalid password (3rd attempt in 10 minutes)',
      ipAddress: '203.0.113.88',
      userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
      location: 'Berlin, DE',
      severity: 'high',
      status: 'failed'
    },
    {
      id: 'log_009',
      timestamp: '2023-08-29 12:08:33',
      user: 'system',
      userType: 'system',
      action: 'Automatic payout processed',
      actionType: 'system',
      entity: 'Payout #P-2023-0829-001',
      entityType: 'payout',
      details: 'Automatic weekly payout processed for Marvel Ads: $847.50 (23 approved conversions)',
      ipAddress: 'system',
      userAgent: 'system',
      severity: 'low',
      status: 'success'
    },
    {
      id: 'log_010',
      timestamp: '2023-08-28 19:22:15',
      user: 'manager2@adbizz.com',
      userType: 'manager',
      action: 'Blocked affiliate',
      actionType: 'block',
      entity: 'Affiliate #507',
      entityType: 'affiliate',
      entityId: 507,
      details: 'Blocked affiliate due to repeated policy violations (Fake traffic detection)',
      ipAddress: '192.168.1.102',
      userAgent: 'Mozilla/5.0 (Macintosh; Intel Mac OS X 10_15_7) AppleWebKit/537.36',
      location: 'Toronto, CA',
      severity: 'critical',
      status: 'success'
    }
  ]);

  const filteredActivities = activities.filter(activity => {
    const searchMatch = activity.user.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       activity.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       activity.entity.toLowerCase().includes(searchTerm.toLowerCase()) ||
                       activity.details.toLowerCase().includes(searchTerm.toLowerCase());

    const dateMatch = activity.timestamp >= `${dateRange.startDate} 00:00:00` &&
                     activity.timestamp <= `${dateRange.endDate} 23:59:59`;
    const userMatch = userFilter === 'all' || activity.userType === userFilter;
    const actionMatch = actionFilter === 'all' || activity.actionType === actionFilter;
    const entityMatch = entityFilter === 'all' || activity.entityType === entityFilter;
    const severityMatch = severityFilter === 'all' || activity.severity === severityFilter;

    return searchMatch && dateMatch && userMatch && actionMatch && entityMatch && severityMatch;
  });

  const calculateStats = () => {
    return {
      total: filteredActivities.length,
      success: filteredActivities.filter(a => a.status === 'success').length,
      failed: filteredActivities.filter(a => a.status === 'failed').length,
      warning: filteredActivities.filter(a => a.status === 'warning').length,
      critical: filteredActivities.filter(a => a.severity === 'critical').length,
      high: filteredActivities.filter(a => a.severity === 'high').length,
      uniqueUsers: new Set(filteredActivities.map(a => a.user)).size
    };
  };

  const stats = calculateStats();

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'success': return 'success';
      case 'failed': return 'danger';
      case 'warning': return 'warning';
      default: return 'secondary';
    }
  };

  const getSeverityClass = (severity: string) => {
    switch (severity) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'secondary';
    }
  };

  const getUserTypeClass = (userType: string) => {
    switch (userType) {
      case 'admin': return 'danger';
      case 'manager': return 'warning';
      case 'affiliate': return 'info';
      case 'advertiser': return 'success';
      case 'system': return 'dark';
      default: return 'secondary';
    }
  };

  const getActionIcon = (actionType: string) => {
    switch (actionType) {
      case 'create': return '➕';
      case 'update': return '✏️';
      case 'delete': return '🗑️';
      case 'login': return '🔐';
      case 'logout': return '🔓';
      case 'view': return '👁️';
      case 'approve': return '✅';
      case 'reject': return '❌';
      case 'block': return '🚫';
      case 'activate': return '🟢';
      case 'system': return '⚙️';
      default: return '📋';
    }
  };

  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    setDateRange(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const exportData = (format: string) => {
    console.log(`Exporting activity log as ${format}`);
    alert(`Exporting ${filteredActivities.length} activities as ${format}`);
  };

  return (
    <div className="reports-page">
      <div className="page-header">
        <h1>Activity Log</h1>
        <div className="header-actions">
          <button className="btn btn-primary" onClick={() => window.location.reload()}>
            Refresh Data
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="report-summary">
        <div className="summary-card">
          <h3>Total Activities</h3>
          <div className="value">{stats.total.toLocaleString()}</div>
        </div>
        <div className="summary-card">
          <h3>Successful</h3>
          <div className="value approved">{stats.success.toLocaleString()}</div>
        </div>
        <div className="summary-card">
          <h3>Failed</h3>
          <div className="value rejected">{stats.failed.toLocaleString()}</div>
        </div>
        <div className="summary-card">
          <h3>Warnings</h3>
          <div className="value pending">{stats.warning.toLocaleString()}</div>
        </div>
        <div className="summary-card">
          <h3>Critical Issues</h3>
          <div className="value rejected">{stats.critical.toLocaleString()}</div>
        </div>
        <div className="summary-card">
          <h3>High Severity</h3>
          <div className="value pending">{stats.high.toLocaleString()}</div>
        </div>
        <div className="summary-card">
          <h3>Unique Users</h3>
          <div className="value">{stats.uniqueUsers}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="report-filters">
        <div className="filters-row">
          <div className="filter-group">
            <label>Start Date:</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => handleDateChange('startDate', e.target.value)}
              className="date-input"
            />
          </div>
          <div className="filter-group">
            <label>End Date:</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => handleDateChange('endDate', e.target.value)}
              className="date-input"
            />
          </div>
          <div className="filter-group">
            <label>User Type:</label>
            <select
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              className="group-select"
            >
              <option value="all">All Users</option>
              <option value="admin">Admin</option>
              <option value="manager">Manager</option>
              <option value="affiliate">Affiliate</option>
              <option value="advertiser">Advertiser</option>
              <option value="system">System</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Action:</label>
            <select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              className="group-select"
            >
              <option value="all">All Actions</option>
              <option value="create">Create</option>
              <option value="update">Update</option>
              <option value="delete">Delete</option>
              <option value="login">Login</option>
              <option value="logout">Logout</option>
              <option value="view">View</option>
              <option value="approve">Approve</option>
              <option value="reject">Reject</option>
              <option value="block">Block</option>
              <option value="activate">Activate</option>
              <option value="system">System</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Entity:</label>
            <select
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              className="group-select"
            >
              <option value="all">All Entities</option>
              <option value="offer">Offers</option>
              <option value="affiliate">Affiliates</option>
              <option value="advertiser">Advertisers</option>
              <option value="payout">Payouts</option>
              <option value="conversion">Conversions</option>
              <option value="user">Users</option>
              <option value="system">System</option>
              <option value="report">Reports</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Severity:</label>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="status-select"
            >
              <option value="all">All Severity</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>
          <div className="filter-group">
            <label>Search:</label>
            <input
              type="text"
              placeholder="Search activities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        <div className="export-buttons">
          <button className="btn btn-success" onClick={() => exportData('CSV')}>
            CSV
          </button>
          <button className="btn btn-warning" onClick={() => exportData('Excel')}>
            Excel
          </button>
          <button className="btn btn-danger" onClick={() => exportData('PDF')}>
            PDF
          </button>
        </div>
      </div>

      {/* Activity Log Table */}
      <div className="report-content">
        <div className="table-container">
          <table className="report-table">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>User</th>
                <th>Action</th>
                <th>Entity</th>
                <th>Details</th>
                <th>IP Address</th>
                <th>Location</th>
                <th>Severity</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredActivities.map((activity) => (
                <tr key={activity.id}>
                  <td>
                    <div className="datetime-cell">
                      <span className="date">{activity.timestamp.split(' ')[0]}</span>
                      <span className="time">{activity.timestamp.split(' ')[1]}</span>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span className="affiliate-name">{activity.user}</span>
                      <span className={`status-badge ${getUserTypeClass(activity.userType)}`}>
                        {activity.userType.toUpperCase()}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '16px' }}>{getActionIcon(activity.actionType)}</span>
                      <span className="action-text">{activity.action}</span>
                    </div>
                  </td>
                  <td>
                    <div className="offer-cell">
                      <span className="offer-name">{activity.entity}</span>
                      <span className="offer-id">{activity.entityType.toUpperCase()}</span>
                    </div>
                  </td>
                  <td>
                    <div style={{
                      maxWidth: '300px',
                      fontSize: '13px',
                      color: '#a0aec0',
                      lineHeight: '1.4'
                    }}>
                      {activity.details}
                    </div>
                  </td>
                  <td>
                    <span className="click-id">{activity.ipAddress}</span>
                  </td>
                  <td>
                    <span style={{ fontSize: '12px', color: '#a0aec0' }}>
                      {activity.location || '-'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${getSeverityClass(activity.severity)}`}>
                      {activity.severity.toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${getStatusClass(activity.status)}`}>
                      {activity.status.charAt(0).toUpperCase() + activity.status.slice(1)}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button className="btn btn-info btn-sm">View Details</button>
                      {activity.severity === 'critical' && (
                        <button className="btn btn-danger btn-sm">Investigate</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="table-footer">
        <div className="table-info">
          Showing {filteredActivities.length} activities for {dateRange.startDate} to {dateRange.endDate}
        </div>
        <div className="pagination">
          <button className="page-btn">Previous</button>
          <button className="page-btn active">1</button>
          <button className="page-btn">2</button>
          <button className="page-btn">3</button>
          <button className="page-btn">Next</button>
        </div>
      </div>
    </div>
  );
};

export default ActivityLog;